import SimulationResumeChoice from '../components/SimulationResumeChoice';
import React from 'react';
import { Play, Pause } from 'lucide-react';
import { SimulationController } from '../hooks/useSimulationController';

interface Props {
  controller: SimulationController;
}

const SimulationTestDesktop: React.FC<Props> = ({ controller }) => {
  const {
    navigate,
    HSK_LEVEL,
    currentConfig,
    UNKNOWN_ANSWER,
    loading,
    initializingApi,
    error,
    showResults,
    scoreSummary,
    currentTest,
    isPlaying,
    audioElement,
    audioPlayedMap,
    groupSelections,
    writingAnswers,
    isSubmitting,
    isCheckpointSaving,
    isManualSaving,
    checkpointError,
    saveProgressMessage,
    saveProgressComplete,
    isListeningPart,
    isReadingPart,
    isSequenceQuestion,
    isTextEntryQuestion,
    isLongFormWritingQuestion,
    isVerticalSequenceQuestion,
    isImagePath,
    resolveQuestionImagePath,
    stripImagePathFromText,
    toMediaUrl,
    getTokens,
    getTokenCounts,
    renderText,
    handleAnswer,
    handleNext,
    handleManualSave,
    previousQuestion,
    playAudio,
    selectToken,
    removeToken,
    onInputChange,
    onDragStart,
    onDragOver,
  } = controller;
  // Touch unused variables for now (handled by isReadingPart logic in original code paths)
  void HSK_LEVEL;
  void isReadingPart;

  if (showResults && scoreSummary) {
    const resultScoreCards = [
      { key: 'listening', label: 'Listening', value: scoreSummary.listening, className: 'bg-blue-50 text-blue-700' },
      { key: 'reading', label: 'Reading', value: scoreSummary.reading, className: 'bg-emerald-50 text-emerald-700' },
      ...(currentConfig.hasWriting ? [{ key: 'writing', label: 'Writing', value: scoreSummary.writing, className: 'bg-purple-50 text-purple-700' }] : []),
    ];
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white p-8 rounded-xl shadow space-y-6">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-3">{renderText(`${currentConfig.title}成绩`)}</h1>
            <div className="text-4xl font-bold text-blue-700">{scoreSummary.total.toFixed(1)} / {scoreSummary.max}</div>
          </div>

          <div className={`grid grid-cols-1 ${currentConfig.hasWriting ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
            {resultScoreCards.map((card) => {
              const [bgClass, textClass] = card.className.split(' ');
              return (
                <div key={card.key} className={`rounded-lg ${bgClass} p-4 text-center`}>
                  <div className="text-sm text-gray-600">{card.label}</div>
                  <div className={`text-2xl font-bold ${textClass}`}>{card.value.toFixed(1)}</div>
                </div>
              );
            })}
          </div>

          <div className="text-center text-sm text-gray-500">{renderText('如需查看回顾，请到 History 页面查看。')}</div>

          <div className="flex justify-center gap-4">
            <button onClick={() => navigate('/history')} className="px-5 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
              {renderText('查看 History')}
            </button>
            <button onClick={() => navigate('/')} className="px-5 py-2 border rounded hover:bg-gray-50">
              {renderText('返回面板')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (controller.resumeChoices) return <SimulationResumeChoice records={controller.resumeChoices} onResume={controller.chooseResume} onNew={controller.chooseNewSimulation} onCancel={() => navigate('/')} />;

  if (loading || initializingApi) return <div className="p-10 text-center">Loading Simulation...</div>;
  if (error && !controller.sessionReady) return <div className="p-10 text-center text-red-600">{error}</div>;
  if (!currentTest) return <div className="p-10 text-center">No test loaded</div>;

  const currentQuestion = currentTest.questions[currentTest.currentQuestionIndex];
  const progress = ((currentTest.currentQuestionIndex + 1) / currentTest.questions.length) * 100;
  const questionImagePath = resolveQuestionImagePath(currentQuestion, HSK_LEVEL);
  const questionText = stripImagePathFromText(currentQuestion.question);

  const renderChoiceOptions = (hasImageOptions: boolean) => {
    const gridClass = hasImageOptions ? 'grid grid-cols-2 gap-4' : 'space-y-3';
    return (
      <div className={gridClass}>
        {currentQuestion.options.map((option, index) => {
          const isUsedInGroup = groupSelections.includes(index);
          const isSelected = currentTest.answers[currentTest.currentQuestionIndex] === index;
          const imageOption = isImagePath(option);

          return (
            <button
              key={index}
              onClick={() => handleAnswer(index)}
              className={`w-full text-left p-4 border rounded-lg ${
                isSelected ? 'border-blue-500 bg-blue-50' : isUsedInGroup ? 'border-red-500 bg-gray-50' : 'border-gray-300 hover:bg-gray-50'
              } ${hasImageOptions ? 'flex flex-col items-center justify-center h-full' : ''}`}
            >
              <div className={`relative w-full ${hasImageOptions ? 'text-center pt-5' : ''}`}>
                <span className={`font-bold text-blue-600 ${hasImageOptions ? 'absolute top-0 left-0' : 'mr-2'}`}>
                  {String.fromCharCode(65 + index)}.
                </span>
                {imageOption ? (
                  <img src={toMediaUrl(option)} className={`h-32 w-full object-contain ${isUsedInGroup ? 'opacity-50' : ''}`} />
                ) : (
                  renderText(option, isUsedInGroup ? 'line-through text-gray-400' : '')
                )}
                {isUsedInGroup && (
                  <span className="absolute top-0 right-0 text-xs bg-red-100 text-red-600 px-1 rounded">{renderText('已选')}</span>
                )}
              </div>
            </button>
          );
        })}
        <button
          onClick={() => handleAnswer(UNKNOWN_ANSWER)}
          className={`w-full text-left p-4 border rounded-lg ${
            currentTest.answers[currentTest.currentQuestionIndex] === UNKNOWN_ANSWER ? 'border-orange-500 bg-orange-50' : 'border-gray-300'
          } ${hasImageOptions ? 'flex flex-col items-center justify-center h-full' : ''}`}
        >
          <div className={`relative w-full ${hasImageOptions ? 'text-center pt-5' : ''}`}>
            <span className={`font-bold text-orange-600 ${hasImageOptions ? 'absolute top-0 left-0' : 'mr-2'}`}>
              {String.fromCharCode(65 + currentQuestion.options.length)}.
            </span>
            <span className="font-medium">{renderText('不知道')}</span>
          </div>
        </button>
      </div>
    );
  };

  const renderSequenceComposer = () => {
    const wa = writingAnswers[currentTest.currentQuestionIndex] || {};
    const tokens = getTokens(currentQuestion);
    const usedTokens = wa.sequence || [];
    const verticalLayout = isVerticalSequenceQuestion(currentQuestion);

    const totalCounts = getTokenCounts(tokens);
    const usedCounts = getTokenCounts(usedTokens);

    const remaining: string[] = [];
    Object.keys(totalCounts).forEach(t => {
      for (let i = 0; i < (totalCounts[t] - (usedCounts[t] || 0)); i++) remaining.push(t);
    });

    return (
      <div>
        <div className="mb-4 text-sm text-gray-600">
          {verticalLayout ? '点击句子添加到答案区，拖动调整顺序' : '点击词语添加到答案区，拖动调整顺序'}
        </div>
        <div className={verticalLayout ? 'space-y-3 mb-4' : 'flex flex-wrap gap-2 mb-4'}>
          {remaining.map((t, i) => (
            <button
              key={`${t}-${i}`}
              onClick={() => selectToken(t)}
              className={verticalLayout ? 'block w-full rounded border bg-gray-50 px-4 py-3 text-left hover:bg-gray-100 transition-colors' : 'px-3 py-1 border rounded bg-gray-50 hover:bg-gray-100 transition-colors'}
            >
              {renderText(t)}
            </button>
          ))}
        </div>
        <div className={verticalLayout ? 'p-4 border rounded bg-blue-50 min-h-16 space-y-3' : 'p-4 border rounded bg-blue-50 min-h-16 flex flex-wrap gap-2'}>
          {usedTokens.map((t, i) => (
            <div
              key={`${t}-${i}`}
              draggable
              onDragStart={(e) => onDragStart(e, i)}
              onDragOver={(e) => onDragOver(e, i)}
              className={verticalLayout ? 'flex w-full items-start justify-between rounded bg-blue-200 px-4 py-3 cursor-move hover:bg-blue-300 transition-colors' : 'px-3 py-1 bg-blue-200 rounded flex items-center cursor-move hover:bg-blue-300 transition-colors'}
            >
              <span className={verticalLayout ? 'pr-3 text-left' : ''}>{renderText(t)}</span>
              <button onClick={(e) => { e.stopPropagation(); removeToken(i); }} className={verticalLayout ? 'ml-3 shrink-0 text-blue-500 hover:text-blue-700' : 'ml-2 text-blue-500 hover:text-blue-700'}>×</button>
            </div>
          ))}
          {usedTokens.length === 0 && (
            <span className="text-gray-400 italic">
              {verticalLayout ? '点击上方句子作答...' : '点击上方词语作答...'}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isSequenceQuestion(currentQuestion)) return renderSequenceComposer();
    if (isListeningPart(currentQuestion) || isReadingPart(currentQuestion)) {
      const hasImageOptions = currentQuestion.options?.some(isImagePath) ?? false;
      return renderChoiceOptions(hasImageOptions);
    }
    if (isTextEntryQuestion(currentQuestion)) {
      const wa = writingAnswers[currentTest.currentQuestionIndex] || {};
      return (
        <div>
          <label className="block text-sm mb-2">
            {isLongFormWritingQuestion(currentQuestion) ? '请根据题目和图片造句：' : '请输入:'}
          </label>
          {isLongFormWritingQuestion(currentQuestion) ? (
            <textarea
              className="w-full border p-3 rounded min-h-32 resize-y"
              value={wa.text || ''}
              onChange={onInputChange}
              placeholder="根据题目和图片写一句完整的句子..."
            />
          ) : (
            <input type="text" className="w-full border p-2 rounded" value={wa.text || ''} onChange={onInputChange} placeholder="在此输入..." />
          )}
        </div>
      );
    }
    return <div>Unknown Part</div>;
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex justify-between items-center mb-2">
            <div>
              <h1 className="text-xl font-bold text-blue-900">{renderText(currentConfig.title)}</h1>
              <div className="text-sm text-blue-600">{currentConfig.description}</div>
            </div>
            <div className="text-sm text-blue-600 font-medium">
              {renderText(`题号: ${currentTest.currentQuestionIndex + 1} / ${currentTest.questions.length}`)}
            </div>
          </div>
          <div className="w-full bg-blue-200 h-3 rounded-full">
            <div className="bg-blue-600 h-3 rounded-full transition-all" style={{ width: `${progress}%` }}></div>
          </div>
        </div>

        <div className="p-6">
          {isListeningPart(currentQuestion) && currentQuestion.media && (
            <div className="mb-6 p-4 bg-blue-50 rounded flex items-center">
              <button
                onClick={playAudio}
                disabled={audioElement ? audioPlayedMap[audioElement.src] : false}
                className={`flex items-center px-4 py-2 rounded text-white ${audioElement && audioPlayedMap[audioElement.src] ? 'bg-gray-400' : 'bg-blue-600'}`}
              >
                {isPlaying ? <Pause className="mr-2" /> : <Play className="mr-2" />}
                {renderText(audioElement && audioPlayedMap[audioElement.src] ? '已播放' : '播放音频')}
              </button>
              <span className="ml-4 text-sm text-gray-600">{renderText('注意: 音频只能播放一次')}</span>
            </div>
          )}

          {questionImagePath && (
            <div className="mb-6 flex justify-center rounded-lg border border-gray-200 bg-gray-50 p-4">
              <img src={toMediaUrl(questionImagePath)} className="max-h-72 w-full object-contain" />
            </div>
          )}

          {!isSequenceQuestion(currentQuestion) && questionText && (
            <div className="mb-6 text-lg font-medium whitespace-pre-line">
              {renderText(questionText.replace(/★/g, '\n★'))}
            </div>
          )}

          {renderContent()}

          {error && (
            <div role="alert" className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg border border-red-200">
              {renderText(error)}
            </div>
          )}
          {checkpointError && (
            <div className="mt-6 p-4 bg-red-50 text-red-600 rounded-lg border border-red-200">
              {renderText(checkpointError)}
            </div>
          )}

          {saveProgressMessage && (
            <div role="status" className={`mt-4 p-3 rounded-lg border ${saveProgressComplete ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}>
              {renderText(saveProgressMessage)}
            </div>
          )}

          <div className="mt-8 flex justify-between gap-3">
            <div className="flex gap-3">
              <button
                onClick={previousQuestion}
                disabled={currentTest.currentQuestionIndex === 0 || isSubmitting || isCheckpointSaving || isManualSaving}
                className="px-4 py-2 border rounded hover:bg-gray-100 disabled:opacity-50"
              >
                {renderText('上一题')}
              </button>
              <button
                onClick={handleManualSave}
                disabled={isSubmitting || isCheckpointSaving || isManualSaving}
                className="px-4 py-2 border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50 disabled:opacity-50"
              >
                {renderText(isManualSaving ? '保存中...' : '保存进度')}
              </button>
            </div>
            <button
              onClick={handleNext}
              disabled={isSubmitting || isCheckpointSaving || isManualSaving}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:hover:bg-blue-600"
            >
              {renderText(isSubmitting ? '提交中...' : isCheckpointSaving || isManualSaving ? '保存进度中...' : (currentTest.currentQuestionIndex === currentTest.questions.length - 1 ? '提交试卷' : '下一题'))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulationTestDesktop;
